import { useEventListener, useTimeoutFn } from '@vueuse/core'

import type { FiltersBarProps } from '../types/interfaces'

interface FiltersBarOptions {
  props: FiltersBarProps
  onSubmit: () => void
}

interface FiltersBarReturn {
  handleFieldsEnter: (event: KeyboardEvent) => void
}

const OPEN_LIST_SELECTOR = '[aria-expanded="true"]'
const OPEN_MODAL_SELECTOR = '.def-overlay, [role="dialog"], [aria-modal="true"]'

/**
 * Enter searches, as the Search button does: in any field (a closed dropdown or date picker searches instead of
 * opening), and with nothing visible focused, which is where a dropdown leaves the focus after a pick and a closed date
 * picker leaves it on its hidden menu. Only a list that is already open keeps Enter, to pick its highlighted option; an
 * open modal keeps the page's Enter. A native select keeps its Enter too (it may confirm an option in its own list), and
 * the search follows on the next task, once the select has applied the value.
 */
export const useFiltersBar = ({ props, onSubmit }: FiltersBarOptions): FiltersBarReturn => {
  const isInOpenList = (event: KeyboardEvent): boolean => {
    const fields = event.currentTarget as Element | null
    const openList = event.target instanceof Element ? event.target.closest(OPEN_LIST_SELECTOR) : null

    return !!openList && !!fields?.contains(openList)
  }

  const isNothingFocused = (): boolean => {
    const focused = document.activeElement

    return !focused || focused === document.body || focused.getClientRects().length === 0
  }

  const submit = (): void => {
    if (props.loading) return

    onSubmit()
  }

  const { start: submitAfterSelect } = useTimeoutFn(submit, 0, { immediate: false })

  const handleFieldsEnter = (event: KeyboardEvent): void => {
    if (event.isComposing || isInOpenList(event)) return

    if (event.target instanceof HTMLSelectElement) {
      submitAfterSelect()
      return
    }

    event.preventDefault()
    event.stopPropagation()
    submit()
  }

  const handlePageEnter = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter' || event.defaultPrevented || event.isComposing) return
    if (!isNothingFocused() || document.querySelector(OPEN_MODAL_SELECTOR)) return

    event.preventDefault()
    submit()
  }

  useEventListener(document, 'keydown', handlePageEnter)

  return {
    handleFieldsEnter,
  }
}
